import React, { useState } from 'react';
import { Offer, OfferType, OfferStatus } from '../../types';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Card, CardContent } from '../ui/Card';
import { Plus, Trash2, ArrowLeft } from 'lucide-react';
interface OfferFormProps {
  type: OfferType;
  initialData?: Offer;
  onSave: (offer: Partial<Offer>) => void;
  onCancel: () => void;
}
export function OfferForm({
  type,
  initialData,
  onSave,
  onCancel
}: OfferFormProps) {
  const [formData, setFormData] = useState<Partial<Offer>>(initialData || {
    type,
    status: 'active',
    locations: [],
    // Type specific defaults
    ...(type === 'fixed' && {
      cashbackPercentage: 0
    }),
    ...(type === 'tiered' && {
      tiers: [{
        minAmount: 0,
        maxAmount: null,
        cashbackPercentage: 0
      }]
    }),
    ...(type === 'promotional' && {
      standardPercentage: 0,
      promotionalPercentage: 0,
      startDate: '',
      endDate: ''
    }),
    ...(type === 'category' && {
      categories: [{
        name: '',
        cashbackPercentage: 0
      }]
    })
  });
  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  // Tiered Offer Helpers
  const addTier = () => {
    if (formData.type !== 'tiered') return;
    const tiers = [...(formData.tiers || [])];
    tiers.push({
      minAmount: 0,
      maxAmount: null,
      cashbackPercentage: 0
    });
    handleChange('tiers', tiers);
  };
  const removeTier = (index: number) => {
    if (formData.type !== 'tiered') return;
    const tiers = [...(formData.tiers || [])];
    tiers.splice(index, 1);
    handleChange('tiers', tiers);
  };
  const updateTier = (index: number, field: string, value: any) => {
    if (formData.type !== 'tiered') return;
    const tiers = [...(formData.tiers || [])];
    tiers[index] = {
      ...tiers[index],
      [field]: value
    };
    handleChange('tiers', tiers);
  };
  // Category Offer Helpers
  const addCategory = () => {
    if (formData.type !== 'category') return;
    const categories = [...(formData.categories || [])];
    categories.push({
      name: '',
      cashbackPercentage: 0
    });
    handleChange('categories', categories);
  };
  const removeCategory = (index: number) => {
    if (formData.type !== 'category') return;
    const categories = [...(formData.categories || [])];
    categories.splice(index, 1);
    handleChange('categories', categories);
  };
  const updateCategory = (index: number, field: string, value: any) => {
    if (formData.type !== 'category') return;
    const categories = [...(formData.categories || [])];
    categories[index] = {
      ...categories[index],
      [field]: value
    };
    handleChange('categories', categories);
  };
  return <div className="space-y-6 max-w-3xl" data-id="element-73">
      <div className="flex items-center gap-4 mb-6" data-id="element-74">
        <Button variant="ghost" size="sm" onClick={onCancel} data-id="element-75">
          <ArrowLeft className="h-4 w-4 mr-2" data-id="element-76" />
          Back
        </Button>
        <h2 className="text-2xl font-bold text-slate-900" data-id="element-77">
          {initialData ? 'Edit Offer' : `Create ${type.charAt(0).toUpperCase() + type.slice(1)} Offer`}
        </h2>
      </div>

      <Card data-id="element-78">
        <CardContent className="space-y-6 pt-6" data-id="element-79">
          {/* Common Fields */}
          <div className="grid gap-6 md:grid-cols-2" data-id="element-80">
            <Input label="Offer Name" placeholder="e.g. Summer Sale" value={formData.name || ''} onChange={e => handleChange('name', e.target.value)} data-id="element-81" />
            <Select label="Status" options={[{
            value: 'active',
            label: 'Active'
          }, {
            value: 'inactive',
            label: 'Inactive'
          }, {
            value: 'scheduled',
            label: 'Scheduled'
          }]} value={formData.status} onChange={e => handleChange('status', e.target.value)} data-id="element-82" />
            <div className="col-span-2" data-id="element-83">
              <label className="text-sm font-medium text-slate-700 mb-2 block" data-id="element-84">
                Description
              </label>
              <textarea className="w-full min-h-[100px] rounded-md border border-slate-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#218080]" placeholder="Describe the offer details..." value={formData.description || ''} onChange={e => handleChange('description', e.target.value)} data-id="element-85" />
            </div>
          </div>

          {/* Type Specific Fields */}
          <div className="border-t border-slate-200 pt-6" data-id="element-86">
            <h3 className="text-lg font-medium text-slate-900 mb-4" data-id="element-87">
              Offer Details
            </h3>

            {type === 'fixed' && <div className="max-w-xs" data-id="element-88">
                <Input type="number" label="Cashback Percentage" placeholder="8" value={formData.cashbackPercentage} onChange={e => handleChange('cashbackPercentage', Number(e.target.value))} helperText="Enter a value between 0 and 100" data-id="element-89" />
              </div>}

            {type === 'tiered' && <div className="space-y-4" data-id="element-90">
                {formData.tiers?.map((tier, index) => <div key={index} className="flex items-end gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200" data-id="element-91">
                    <Input type="number" label="Min Amount ($)" value={tier.minAmount} onChange={e => updateTier(index, 'minAmount', Number(e.target.value))} className="bg-white" data-id="element-92" />
                    <Input type="number" label="Max Amount ($)" placeholder="Unlimited" value={tier.maxAmount || ''} onChange={e => updateTier(index, 'maxAmount', e.target.value ? Number(e.target.value) : null)} className="bg-white" data-id="element-93" />
                    <Input type="number" label="Cashback %" value={tier.cashbackPercentage} onChange={e => updateTier(index, 'cashbackPercentage', Number(e.target.value))} className="bg-white" data-id="element-94" />
                    <Button variant="ghost" size="icon" onClick={() => removeTier(index)} className="text-red-500 hover:text-red-600 hover:bg-red-50 mb-0.5" disabled={formData.tiers?.length === 1} data-id="element-95">
                      <Trash2 className="h-4 w-4" data-id="element-96" />
                    </Button>
                  </div>)}
                <Button variant="outline" onClick={addTier} className="w-full border-dashed" data-id="element-97">
                  <Plus className="h-4 w-4 mr-2" data-id="element-98" /> Add Tier
                </Button>
              </div>}

            {type === 'promotional' && <div className="grid gap-6 md:grid-cols-2" data-id="element-99">
                <Input type="number" label="Standard Cashback %" value={formData.standardPercentage} onChange={e => handleChange('standardPercentage', Number(e.target.value))} data-id="element-100" />
                <Input type="number" label="Promotional Cashback %" value={formData.promotionalPercentage} onChange={e => handleChange('promotionalPercentage', Number(e.target.value))} data-id="element-101" />
                <Input type="date" label="Start Date" value={formData.startDate} onChange={e => handleChange('startDate', e.target.value)} data-id="element-102" />
                <Input type="date" label="End Date" value={formData.endDate} onChange={e => handleChange('endDate', e.target.value)} data-id="element-103" />
              </div>}

            {type === 'category' && <div className="space-y-4" data-id="element-104">
                {formData.categories?.map((cat, index) => <div key={index} className="flex items-end gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200" data-id="element-105">
                    <Input label="Category Name" placeholder="e.g. Installation" value={cat.name} onChange={e => updateCategory(index, 'name', e.target.value)} className="bg-white flex-1" data-id="element-106" />
                    <Input type="number" label="Cashback %" value={cat.cashbackPercentage} onChange={e => updateCategory(index, 'cashbackPercentage', Number(e.target.value))} className="bg-white w-32" data-id="element-107" />
                    <Button variant="ghost" size="icon" onClick={() => removeCategory(index)} className="text-red-500 hover:text-red-600 hover:bg-red-50 mb-0.5" disabled={formData.categories?.length === 1} data-id="element-108">
                      <Trash2 className="h-4 w-4" data-id="element-109" />
                    </Button>
                  </div>)}
                <Button variant="outline" onClick={addCategory} className="w-full border-dashed" data-id="element-110">
                  <Plus className="h-4 w-4 mr-2" data-id="element-111" /> Add Category
                </Button>
              </div>}
          </div>

          {/* Locations */}
          <div className="border-t border-slate-200 pt-6" data-id="element-112">
            <h3 className="text-lg font-medium text-slate-900 mb-4" data-id="element-113">
              Participating Locations
            </h3>
            <div className="grid gap-3 md:grid-cols-2" data-id="element-114">
              {['Head Office - New York', 'Branch - Brooklyn', 'Branch - Queens', 'Branch - Staten Island'].map((loc, i) => <label key={i} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50" data-id="element-115">
                  <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-[#218080] focus:ring-[#218080]" checked={formData.locations?.includes(`loc_${i + 1}`)} onChange={e => {
                const current = formData.locations || [];
                if (e.target.checked) {
                  handleChange('locations', [...current, `loc_${i + 1}`]);
                } else {
                  handleChange('locations', current.filter(l => l !== `loc_${i + 1}`));
                }
              }} data-id="element-116" />
                  <span className="text-sm text-slate-700" data-id="element-117">{loc}</span>
                </label>)}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-4" data-id="element-118">
        <Button variant="ghost" onClick={onCancel} data-id="element-119">
          Cancel
        </Button>
        <Button onClick={() => onSave(formData)} className="bg-[#218080] hover:bg-[#1a6666]" data-id="element-120">
          {initialData ? 'Save Changes' : 'Create Offer'}
        </Button>
      </div>
    </div>;
}